import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Camera, CheckCircle2, Download, IdCard, Loader2,
  Package, ScanLine, Sparkles, Plus, Trash2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * 4-5 step onboarding wizard for new staff.
 *
 * 1. Photo upload    — capture or upload, see live preview
 * 2. Profile         — title, dept, employment type, start date, emergency contact
 * 3. Hardware        — assign existing assets (by tag/UPC) to the employee
 * 4. In-service      — (aides only) set annual target + schedule initial training
 * 5. Badge + finish  — download PDF, link to employee page
 *
 * Each step persists immediately so partial progress isn't lost on refresh.
 */
export default function HROnboardingPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState({});
  const [assignedAssets, setAssignedAssets] = useState([]);
  const [scan, setScan] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [d, a] = await Promise.all([
        axios.get(`${API_URL}/api/hr/employees/${userId}`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/hr/employees/${userId}/assets`, { headers: authHeader() }).catch(() => ({ data: { assets: [] } })),
      ]);
      setData(d.data);
      setProfile(d.data?.profile || {});
      setAssignedAssets(a.data?.assets || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  // ---------- Step 1: photo ----------
  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error('Photo too large (max 2.5MB)'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await axios.put(`${API_URL}/api/hr/employees/${userId}/photo`,
          { photo_base64: String(reader.result) }, { headers: authHeader() });
        toast.success('Photo uploaded');
        load();
      } catch (err) { toast.error('Upload failed'); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  // ---------- Step 2: profile ----------
  const saveProfile = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.keys(profile).forEach(k => { if (profile[k] != null && profile[k] !== '') payload[k] = profile[k]; });
      await axios.put(`${API_URL}/api/hr/employees/${userId}`, payload, { headers: authHeader() });
      toast.success('Profile saved');
      await load();
      setStep(3);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  // ---------- Step 3: hardware ----------
  const assignAsset = async () => {
    const code = scan.trim();
    if (!code) { toast.error('Enter or scan a code'); return; }
    setBusy(true);
    try {
      // Look up the asset by tag / serial / UPC
      const lookup = await axios.get(`${API_URL}/api/assets/lookup/${encodeURIComponent(code)}`, { headers: authHeader() });
      const asset = lookup.data;
      await axios.patch(`${API_URL}/api/assets/${asset.id}`,
        { status: 'assigned', assigned_to_user_id: userId },
        { headers: authHeader() });
      toast.success(`Assigned ${asset.asset_tag}`);
      setScan('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `No asset matches "${code}"`);
    } finally { setBusy(false); }
  };

  const unassignAsset = async (asset) => {
    if (!window.confirm(`Unassign ${asset.asset_tag}?`)) return;
    try {
      await axios.patch(`${API_URL}/api/assets/${asset.id}`,
        { status: 'in_stock', assigned_to_user_id: null }, { headers: authHeader() });
      toast.success('Unassigned');
      load();
    } catch (err) { toast.error('Failed'); }
  };

  // ---------- Step 4: badge ----------
  const downloadBadge = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/hr/employees/${userId}/badge.pdf`,
        { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `badge-${(data?.name || userId).replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Badge downloaded');
    } catch (err) { toast.error('Could not generate badge'); }
  };

  if (!data) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;

  const stripe = data.stripe_color;
  const setP = (k, v) => setProfile(f => ({ ...f, [k]: v }));
  const photoUrl = data.photo_data_url;
  const step1Done = !!photoUrl;
  const step2Done = !!(profile.job_title && profile.department);
  const isAide = data.role === 'aide';

  const steps = isAide ? [
    { n: 1, label: 'Photo', icon: Camera, done: step1Done },
    { n: 2, label: 'Profile', icon: IdCard, done: step2Done },
    { n: 3, label: 'Hardware', icon: Package, done: assignedAssets.length > 0 },
    { n: 4, label: 'In-Service', icon: GraduationCap, done: false },
    { n: 5, label: 'Badge', icon: Download, done: false },
  ] : [
    { n: 1, label: 'Photo', icon: Camera, done: step1Done },
    { n: 2, label: 'Profile', icon: IdCard, done: step2Done },
    { n: 3, label: 'Hardware', icon: Package, done: assignedAssets.length > 0 },
    { n: 4, label: 'Badge', icon: Download, done: false },
  ];
  // Badge step index — moves from 4 to 5 when In-Service is inserted for aides.
  const badgeStep = isAide ? 5 : 4;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="hr-onboarding-page">
      <Link to={`/hr/${userId}`} className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to profile
      </Link>
      <div className="flex items-center justify-between mt-2 mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#0E7490]" /> Onboard {data.name}
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Get them ready for day one in 4 quick steps.</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-6" data-testid="onboarding-stepper">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <button onClick={() => setStep(s.n)}
                    data-testid={`step-${s.n}-btn`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                      step === s.n ? 'bg-[#0E7490] text-white border-[#0E7490]' :
                      s.done ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      'bg-white text-[#64748B] border-[#E2E8F0]'
                    }`}>
              {s.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
              <span>{s.n}. {s.label}</span>
            </button>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-[#E2E8F0]" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — Photo */}
      {step === 1 && (
        <Card className="p-6 border-[#E2E8F0]" data-testid="step-1-card">
          <h2 className="text-base font-semibold text-[#0A2342] mb-2">Step 1 — Upload a clear headshot</h2>
          <p className="text-xs text-[#64748B] mb-4">This appears on their ID badge and profile. JPEG or PNG, &lt; 2.5MB.</p>
          <div className="flex items-center gap-4 flex-wrap">
            {photoUrl ? (
              <img alt="" src={photoUrl} className="w-32 h-32 rounded-md object-cover border border-[#E2E8F0]" />
            ) : (
              <div className="w-32 h-32 rounded-md bg-[#F1F5F9] flex items-center justify-center text-3xl font-bold text-[#94A3B8]">
                {(data.name || '?').split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase()}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ob-upload-btn"
                      className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Camera className="w-4 h-4 mr-1" />}
                {photoUrl ? 'Replace photo' : 'Upload photo'}
              </Button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={handlePhoto} />
              <p className="text-[11px] text-[#94A3B8]">Tip: a front-facing photo, plain background, work-appropriate attire.</p>
            </div>
          </div>
          <div className="flex justify-end mt-4 pt-3 border-t border-[#E2E8F0]">
            <Button variant="outline" onClick={() => setStep(2)} data-testid="ob-next-1">
              Next: Profile <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2 — Profile */}
      {step === 2 && (
        <Card className="p-6 border-[#E2E8F0]" data-testid="step-2-card">
          <h2 className="text-base font-semibold text-[#0A2342] mb-2">Step 2 — Basic employment info</h2>
          <p className="text-xs text-[#64748B] mb-4">Just the essentials — you can fill the rest later on their profile page.</p>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><Label>Job title *</Label><Input value={profile.job_title || ''} onChange={e => setP('job_title', e.target.value)} placeholder="e.g. Registered Nurse" data-testid="ob-title" /></div>
            <div><Label>Department *</Label><Input value={profile.department || ''} onChange={e => setP('department', e.target.value)} placeholder="e.g. Skilled Nursing" data-testid="ob-dept" /></div>
            <div><Label>Employment type</Label>
              <select value={profile.employment_type || ''} onChange={e => setP('employment_type', e.target.value)}
                      className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm">
                <option value="">—</option>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="contractor">Contractor</option>
                <option value="intern">Intern</option>
                <option value="per_diem">Per diem</option>
              </select>
            </div>
            <div><Label>Start date</Label><Input type="date" value={profile.start_date || ''} onChange={e => setP('start_date', e.target.value)} /></div>
            <div><Label>Work email</Label><Input value={profile.work_email || ''} onChange={e => setP('work_email', e.target.value)} placeholder={data.email} /></div>
            <div><Label>Phone</Label><Input value={profile.phone || ''} onChange={e => setP('phone', e.target.value)} /></div>
            <div className="sm:col-span-2 mt-2 pt-3 border-t border-[#E2E8F0]">
              <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Emergency contact</div>
            </div>
            <div><Label>Name</Label><Input value={profile.emergency_contact_name || ''} onChange={e => setP('emergency_contact_name', e.target.value)} /></div>
            <div><Label>Phone</Label><Input value={profile.emergency_contact_phone || ''} onChange={e => setP('emergency_contact_phone', e.target.value)} /></div>
          </div>
          <div className="flex justify-between mt-4 pt-3 border-t border-[#E2E8F0]">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <Button onClick={saveProfile} disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="ob-next-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save &amp; continue <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 3 — Hardware */}
      {step === 3 && (
        <Card className="p-6 border-[#E2E8F0]" data-testid="step-3-card">
          <h2 className="text-base font-semibold text-[#0A2342] mb-2">Step 3 — Assign hardware</h2>
          <p className="text-xs text-[#64748B] mb-4">Scan or type an asset tag (e.g. SVN-LAP-00001) or UPC barcode. Skip if no hardware yet.</p>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <ScanLine className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input className="pl-8 font-mono" value={scan} onChange={e => setScan(e.target.value)}
                     placeholder="SVN-XXX-NNNNN or barcode" data-testid="ob-asset-input"
                     onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); assignAsset(); } }} />
            </div>
            <Button onClick={assignAsset} disabled={busy || !scan.trim()}
                    className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="ob-assign-btn">
              <Plus className="w-4 h-4 mr-1" /> Assign
            </Button>
          </div>
          <div className="space-y-1">
            {assignedAssets.length === 0 ? (
              <p className="text-xs text-[#94A3B8] italic">No hardware assigned yet.</p>
            ) : assignedAssets.map(a => (
              <div key={a.id} className="flex items-center justify-between text-xs border border-[#F1F5F9] rounded px-2 py-1.5">
                <div>
                  <span className="font-mono text-[#0A2342]">{a.asset_tag}</span>
                  <span className="ml-2 text-[#64748B]">{a.name}</span>
                </div>
                <button onClick={() => unassignAsset(a)} className="text-[#991B1B] hover:underline" data-testid={`ob-unassign-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4 pt-3 border-t border-[#E2E8F0]">
            <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <Button onClick={() => setStep(isAide ? 4 : badgeStep)} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="ob-next-3">
              {isAide ? <>Next: In-Service</> : <>Next: Badge</>} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 4 — In-Service (aides only) */}
      {step === 4 && isAide && (
        <Card className="p-6 border-[#E2E8F0]" data-testid="step-4-card">
          <h2 className="text-base font-semibold text-[#0A2342] mb-2 flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-[#0E7490]" /> Step 4 — In-service training
          </h2>
          <p className="text-xs text-[#64748B] mb-4">
            Set the annual hours target and schedule the first session. Aides must
            complete continuing education each year (CMS 484.80 requires ≥12h).
            The aide will receive an in-app notification when scheduled.
          </p>
          <InServiceTrainingCard userId={userId} variant="supervisor" />
          <div className="flex justify-between mt-4 pt-3 border-t border-[#E2E8F0]">
            <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <Button onClick={() => setStep(badgeStep)} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="ob-next-4">
              Next: Badge <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* Step 4 — Badge + Finish */}
      {step === badgeStep && (
        <Card className="p-6 border-[#E2E8F0]" data-testid="step-4-card">
          <h2 className="text-base font-semibold text-[#0A2342] mb-2">Step {badgeStep} — Print ID badge</h2>
          <p className="text-xs text-[#64748B] mb-4">Download the PDF and print on a 3.375" × 2.125" badge card.</p>
          <Card className="p-0 overflow-hidden border-[#E2E8F0] max-w-sm mx-auto">
            <div className="h-2" style={{ background: stripe }} />
            <div className="p-5">
              <div className="flex items-start gap-3">
                {photoUrl ? (
                  <img alt="" src={photoUrl} className="w-20 h-20 rounded-md object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-md bg-[#F1F5F9] flex items-center justify-center text-xl font-bold text-[#64748B]">
                    {(data.name || '?').split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: stripe }}>
                    {data.role?.replace(/_/g, ' ')}
                  </div>
                  <h3 className="text-base font-bold text-[#0A2342] mt-0.5">{data.name}</h3>
                  <p className="text-[11px] text-[#64748B]">{profile.job_title}</p>
                  <p className="text-[10px] text-[#94A3B8]">{profile.department}</p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex justify-between mt-4 pt-3 border-t border-[#E2E8F0]">
            <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadBadge} data-testid="ob-download-badge-btn">
                <Download className="w-4 h-4 mr-1" /> Download PDF
              </Button>
              <Button onClick={() => { toast.success('Onboarding complete!'); navigate(`/hr/${userId}`); }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ob-finish-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Finish
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
